import _get from "lodash/get";
import { getInstitution, getProduct } from './common/general-utils';
import { getNameWithFallbacks } from '../utils';
/**
 * Get the name of the marketplace entity. There are currently no fallbacks.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    The name
 */

export var getName = function getName(marketplaceEntity) {
  return getNameWithFallbacks(marketplaceEntity);
};
/**
 * Get the average APR %.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {number}                    The average APR
 */

export var getAprAverage = function getAprAverage(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.apr_average.value');
};
/**
 * Get the max APR %.
 *
 * @param     {Object} marketplaceEntity  The smb loans entity
 * @return    {number}                    The max APR
 */

export var getAprMax = function getAprMax(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_apr.value');
};
/**
 * Get the min APR %.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {number}                    The min APR
 */

export var getAprMin = function getAprMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_apr.value');
};
/**
 * Get the APR tooltip.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    The APR tooltip
 */

export var getAprTooltip = function getAprTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.apr_tooltip.value');
};
/**
 * Get the bottom line text.
 *
 * @param  {Object} marketplaceEntity   The marketplace entity
 * @return {String}                     The bottom line value.
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value');
};
/**
 * Gets whether this smb loans entity requires collateral.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {bool}                      collateral
 */

export var getCollateral = function getCollateral(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.collateral.value');
};
/**
 * Gets the "cons" bullets
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {Array}                     The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value');
};
/**
 * Get the minimum recommended FICO score.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    The min FICO score
 */

export var getFicoMin = function getFicoMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_fico_displayed.value');
};
/**
 * Gets the name of the entity as the alt text for the associated image
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.image_alt_text.value') || getName(marketplaceEntity);
};
/**
 * Get the source URL of the image associated with the marketplace entity.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    The URL of the image
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.image_source.value');
};
/**
 * Get the issuer
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    Issuer name
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'name');
};
/**
 * Get the learn more link.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    Learn more link
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_link.value');
};
/**
 * Get the learn more link title.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    Learn more link title
 */

export var getLearnMoreLinkTitle = function getLearnMoreLinkTitle(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_title.value');
};
/**
 * Get the legacy id for an entity.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {string}                    The legacy id
 */

export var getLegacyId = function getLegacyId(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.legacy_id.value');
};
/**
 * Get the maximum loan term in months.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {number}                    The maximum loan term in months
 */

export var getLoanTermMax = function getLoanTermMax(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_loan_term.value');
};
/**
 * Gets the "pros" bullets
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {Array}                     The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value');
};
/**
 * Get the qualifications bullets.
 *
 * @param   {Object} marketplaceEntity  The smb loans entity
 * @return  {Array}                     The qualifications bullets
 */

export var getQualificationsBullets = function getQualificationsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.qualifications_bullets.value');
};
/**
 * Get the slug.
 * @todo this field is not yet populated
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The slug
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'external_id');
};
/**
 * Get the star rating.
 *
 * @param   {Object} marketplaceEntity  The education loans entity
 * @return  {number}                    The star rating
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.rating_stars.value');
};